import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Slider } from "@base-ui-components/react";
import LeafRectangleLoader from "../LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { marketCode, storedLocale } from "../../../components/Authentication/util";
import { getNumberFormatter, getLabelFormatter } from "../../util/format";
import "./LeafSlider.scss";

/**
 * Hook to detect if container width requires compact labels
 */
const useCompactLabels = (ref: React.RefObject<HTMLDivElement | null>) => {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    const element = ref.current;
    const checkWidth = () => setIsCompact(element.offsetWidth < 600);

    checkWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(checkWidth);
      observer.observe(element);
      return () => observer.disconnect();
    }

    const handleResize = () => checkWidth();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isCompact;
};

export interface MarkLabels {
  Label: string;
  Color?: string;
}

/**
 * Props interface for LeafSlider component
 */
export type Props = {
  isLoading: boolean;
  base: number;
  value: number;
  min?: number;
  max?: number;
  tooltipSuffixText?: string;
  marks: { Label: string; Value: number }[];
  markLabels?: MarkLabels[];
  onChange: (value: number) => void;
  disabled?: boolean;
  ariaLabel?: string;
};

/**
 * Validate if marks array is ready for calculations
 */
const isMarkReady = (marks?: { Value: number }[]): marks is { Value: number }[] => {
  return Array.isArray(marks) && marks.length >= 2;
};

/**
 * Convert slider percent (0–1) to actual value based on mark segments
 */
const percentToValue = (percent: number, marks: { Value: number }[]) => {
  if (!isMarkReady(marks)) return 0;
  const segments = marks.length - 1;
  const index = Math.min(Math.floor(percent * segments), segments - 1);
  const from = marks[index].Value;
  const to = marks[index + 1].Value;
  const local = percent * segments - index;
  return from + (to - from) * local;
};

/**
 * Convert actual value to slider percent (0–1) based on mark segments
 */
const valueToPercent = (value: number, marks: { Value: number }[]) => {
  if (!isMarkReady(marks)) return 0;
  for (let i = 0; i < marks.length - 1; i++) {
    const from = marks[i].Value;
    const to = marks[i + 1].Value;
    if (value >= from && value <= to) {
      const local = (value - from) / (to - from);
      return (i + local) / (marks.length - 1);
    }
  }
  if (value < marks[0].Value) return 0;
  if (value > marks[marks.length - 1].Value) return 1;
  return 1;
};

/**
 * Custom hook for managing tooltip positioning relative to thumb
 */
const useTooltipPositioning = ({
  thumbElementRef,
  trackWrapperRef,
  tooltipElementRef,
}: {
  thumbElementRef: React.RefObject<HTMLDivElement | null>;
  trackWrapperRef: React.RefObject<HTMLDivElement | null>;
  tooltipElementRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const [tooltipPositionPx, setTooltipPositionPx] = useState("50%");
  const [tooltipArrowPositionPx, setTooltipArrowPositionPx] = useState("50%");

  /**
   * Calculate and update tooltip position based on thumb location
   */
  const recalculateTooltipPosition = () => {
    if (!thumbElementRef.current || !trackWrapperRef.current || !tooltipElementRef.current) {
      return;
    }

    const thumbRect = thumbElementRef.current.getBoundingClientRect();
    const wrapperRect = trackWrapperRef.current.getBoundingClientRect();
    const tooltipWidth = tooltipElementRef.current.offsetWidth;
    const wrapperWidth = wrapperRect.width;

    // Get thumb position relative to wrapper
    const thumbLeft = thumbRect.left - wrapperRect.left;
    const thumbCenter = thumbLeft + thumbRect.width / 2;

    // Center tooltip horizontally above thumb
    let tooltipLeft = thumbCenter - tooltipWidth / 2;

    // Clamp tooltip within track wrapper
    if (tooltipWidth >= wrapperWidth) {
      tooltipLeft = (wrapperWidth - tooltipWidth) / 2;
    } else if (tooltipLeft < 0) {
      tooltipLeft = -20;
    } else if (tooltipLeft + tooltipWidth > wrapperWidth) {
      tooltipLeft = wrapperWidth - tooltipWidth + 10;
    }

    // Arrow should always point to thumb center (relative to tooltip)
    const arrowLeft = thumbCenter - tooltipLeft - 15;

    setTooltipPositionPx(`${tooltipLeft}px`);
    setTooltipArrowPositionPx(`${arrowLeft}px`);
  };

  return {
    tooltipPositionPx,
    tooltipArrowPositionPx,
    recalculateTooltipPosition,
  };
};

/**
 * Generate CSS background for track with base and value indicators
 */
const getTrackBackground = (base: number, value: number, marks: { Value: number }[]) => {
  if (!isMarkReady(marks)) return;
  const basePercent = valueToPercent(base, marks) * 100;
  const valuePercent = valueToPercent(value, marks) * 100;

  return `
    linear-gradient(to right,
      #38539A 0%,
      #38539A ${basePercent}%,
      transparent ${basePercent}%,
      transparent ${valuePercent}%,
      #d6d6d6 ${valuePercent}%,
      #d6d6d6 100%
    ),
    repeating-linear-gradient(
      135deg,
      #38539A,
      #38539A 0.125rem,
      #6074AA 0.125rem,
      #6074AA 0.625rem
    )
  `;
};

/**
 * Extract coordinates from pointer or touch events
 */
const getEventCoordinates = (event: PointerEvent | TouchEvent) => {
  if ("touches" in event && event.touches.length > 0) return event.touches[0];
  if ("changedTouches" in event && event.changedTouches.length > 0) return event.changedTouches[0];
  if ("clientX" in event) return event;
  return { clientX: 0, clientY: 0 };
};

/**
 * Slider component with mark-based segments and tooltip positioning
 */
const LeafSlider = ({
  base,
  value,
  min,
  max,
  tooltipSuffixText = "",
  marks,
  markLabels,
  onChange,
  isLoading,
  disabled,
  ariaLabel,
}: Props) => {
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const trackWrapperRef = useRef<HTMLDivElement>(null);
  const thumbElementRef = useRef<HTMLDivElement>(null);
  const tooltipElementRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);

  // State
  const [sliderPercent, setSliderPercent] = useState(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [isThumbDragging, setIsThumbDragging] = useState(false);
  const [isSliderHovered, setIsSliderHovered] = useState(false);

  // Hooks
  const shouldShowCompactLabels = useCompactLabels(containerRef);

  // Locale setup
  const locale = storedLocale();
  const market = marketCode();

  // Formatters
  const labelFormatter = useCallback(
    (value: number, label: string, index: number) => {
      if (index === 0) return label;
      return getLabelFormatter(locale, market).format(value);
    },
    [locale, market],
  );
  //tooltip formatter
  const tooltipFormatter = useMemo(() => getNumberFormatter(locale, market), [locale, market]);

  // tooltip hook
  const { tooltipPositionPx, tooltipArrowPositionPx, recalculateTooltipPosition } = useTooltipPositioning({
    thumbElementRef,
    trackWrapperRef,
    tooltipElementRef,
  });

  // Sync with external value
  useEffect(() => {
    if (!isMarkReady(marks)) return;
    const newPercent = valueToPercent(value, marks);
    setSliderPercent(newPercent);
    setSliderValue(value);
  }, [value, marks]);

  // Update tooltip position
  useEffect(() => {
    if (isSliderHovered || isThumbDragging) {
      recalculateTooltipPosition();
    }
  }, [sliderValue, isSliderHovered, isThumbDragging]);

  // Handle thumb release
  useEffect(() => {
    if (!isThumbDragging) return;

    const handleThumbRelease = (event: PointerEvent | TouchEvent) => {
      const { clientX, clientY } = getEventCoordinates(event);

      setIsThumbDragging(false);

      const isInside = [containerRef, controlRef].some((ref) => {
        const bounds = ref.current?.getBoundingClientRect();
        return (
          bounds &&
          clientX >= bounds.left &&
          clientX <= bounds.right &&
          clientY >= bounds.top &&
          clientY <= bounds.bottom
        );
      });

      if (!isInside) setIsSliderHovered(false);
    };

    document.addEventListener("pointerup", handleThumbRelease);
    document.addEventListener("touchend", handleThumbRelease);

    return () => {
      document.removeEventListener("pointerup", handleThumbRelease);
      document.removeEventListener("touchend", handleThumbRelease);
    };
  }, [isThumbDragging]);

  // Clamping logic
  const minPercentAllowed = isMarkReady(marks) ? valueToPercent(base, marks) : 0;
  const clampPercent = (percent: number) => Math.max(minPercentAllowed, percent);

  // Handle value changes
  const handleChange = ([percent]: number[]) => {
    if (!isMarkReady(marks) || disabled) return;
    const clampedPercent = clampPercent(percent);
    const rawValue = percentToValue(clampedPercent, marks);
    setSliderPercent(clampedPercent);
    setSliderValue(rawValue);

    // Only trigger onChange if value actually changed
    const isEqual = Math.abs(rawValue - value) < 0.01;
    if (!isEqual) {
      onChange(rawValue);
    }
  };

  // Handle value commit
  const handleCommit = ([percent]: number[]) => {
    if (!isMarkReady(marks) || disabled) return;
    const clampedPercent = clampPercent(percent);
    const rawValue = percentToValue(clampedPercent, marks);

    // Only trigger onChange if value actually changed
    const isEqual = Math.abs(rawValue - value) < 0.01;
    if (!isEqual) {
      onChange(rawValue);
    }
  };

  return (
    <div className={`leaf-slider${disabled ? " leaf-slider--disabled" : ""}`} ref={containerRef}>
      <LeafRectangleLoader isLoading={isLoading} height="3rem" width="auto">
        <div className="leaf-slider__wrapper" ref={trackWrapperRef}>
          <Slider.Root
            value={[sliderPercent]}
            min={0}
            max={1}
            step={0.01}
            onValueChange={handleChange}
            onValueCommitted={handleCommit}
            disabled={disabled}
            onMouseEnter={() => {
              if (!disabled) {
                setIsSliderHovered(true);
              }
            }}
            onMouseLeave={() => {
              if (!disabled) {
                setIsSliderHovered(false);
              }
            }}
            className="leaf-slider__root"
          >
            <Slider.Control className="leaf-slider__control" ref={controlRef}>
              <Slider.Track
                className="leaf-slider__track"
                style={{ background: getTrackBackground(base, sliderValue, marks) }}
              >
                {/* Visual extension bar */}
                <div className="leaf-slider__track-extension-right" aria-hidden="true" />
                <div
                  className={`leaf-slider__marks${markLabels ? " leaf-slider__marks--with-marker-label" : ""}`}
                  aria-hidden="true"
                >
                  {marks.map(({ Label, Value }, index) => {
                    const markLabel = markLabels?.[index];
                    const markLabelColor = markLabel?.Color ? ` leaf-slider__mark-label-top--${markLabel.Color}` : "";
                    // marks are visually evenly spaced across the track
                    const leftPercent = marks.length > 1 ? (index / (marks.length - 1)) * 100 : 0;
                    return (
                      <div
                        key={Label}
                        className={"leaf-slider__mark"}
                        style={{ left: `${leftPercent}%`, transform: "translateX(-50%)" }}
                      >
                        {markLabel && (
                          <div
                            className={`leaf-slider__mark-label-top${markLabelColor}`}
                            aria-hidden="true"
                            style={{
                              transform:
                                index === 0 ? "translateX(50%)" : index === marks.length - 1 ? "translateX(-10%)" : "",
                            }}
                          >
                            {markLabel.Label}
                          </div>
                        )}
                        <div
                          className={`leaf-slider__mark-tick${
                            Value > sliderValue ? " leaf-slider__mark-tick--show" : ""
                          }`}
                        />
                        <div className="leaf-slider__mark-label-bottom">
                          <div
                            className="leaf-slider__mark-label-wrapper"
                            style={{
                              transform:
                                index === 0 ? "translateX(50%)" : index === marks.length - 1 ? "translateX(-10%)" : "",
                            }}
                            aria-hidden="true"
                          >
                            {shouldShowCompactLabels ? (
                              <span className="leaf-slider__mark-label-text">
                                {index === 0 && isNaN(Number(Label)) ? (
                                  <>
                                    <span>{Value}</span>
                                    <span>{Label}</span>
                                  </>
                                ) : (
                                  labelFormatter(Value, Label, index)
                                )}
                              </span>
                            ) : (
                              <span className="leaf-slider__mark-label-text">{Label}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!disabled &&
                  (isThumbDragging || isSliderHovered) &&
                  (Math.abs(value - base) < 0.01 || value > base) && (
                    <div
                      ref={tooltipElementRef}
                      className={`leaf-slider__tooltip${markLabels ? " leaf-slider__tooltip--with-marker-label" : ""}`}
                      style={{ left: tooltipPositionPx }}
                    >
                      {tooltipFormatter.format(sliderValue)} {tooltipSuffixText}
                      <div
                        className="leaf-slider__tooltip-arrow"
                        style={{ left: tooltipArrowPositionPx }}
                        aria-hidden="true"
                      />
                    </div>
                  )}
                <Slider.Thumb
                  tabIndex={-1}
                  ref={thumbElementRef}
                  className="leaf-slider__thumb"
                  getAriaLabel={() => ariaLabel || "Slider"}
                  onTouchStart={() => !disabled && setIsThumbDragging(true)}
                  onPointerDown={() => !disabled && setIsThumbDragging(true)}
                />
              </Slider.Track>
            </Slider.Control>
          </Slider.Root>
        </div>
      </LeafRectangleLoader>
    </div>
  );
};

export default LeafSlider;
